import React from 'react';
import {
  FormControl,
  Select,
  SelectProps,
  FormHelperText,
  CircularProgress,
  Box,
  Typography,
  InputLabel,
  Autocomplete,
  TextField
} from '@mui/material';
import pallet from '../../layouts/pallet';
import { FormInputLabel } from '../MultiSelectWithAll/styled';
import CustomMenuItem from '../CustomMenuItem/CustomMenuItem';
import Placeholder from '../Placeholder/Placeholder';
import { fontSizes } from '../../layouts/fontSizes';
import Icons from '../icons';

export interface DropdownSelectOption {
  value: string;
  label: string | React.ReactNode;
  inputValue?: string;
}

interface DropdownSelectProps extends Omit<SelectProps, 'onChange' | 'value'> {
  label: string;
  value: string;
  options: DropdownSelectOption[];
  onChange: (
    event:
      | React.ChangeEvent<HTMLInputElement>
      | (Event & { target: { value: unknown; name: string } }),
    child?: React.ReactNode
  ) => void;
  required?: boolean;
  disabled?: boolean;
  name: string;
  loading?: boolean;
  hasTopLabel?: boolean;
  helperText?: string;
  hintText?: string;
  placeholder?: string;
  isSearchable?: boolean;
}

const DropdownSelect = ({
  label,
  value,
  options,
  onChange,
  required,
  disabled,
  name,
  loading,
  hasTopLabel = true,
  helperText,
  hintText,
  placeholder,
  isSearchable = false,
  ...selectProps
}: DropdownSelectProps) => {
  const DefaultCaret = Icons.CaretDown;
  const IconComp = loading
    ? () => null
    : selectProps.IconComponent || DefaultCaret;

  const anchorRef = React.useRef<HTMLDivElement | null>(null);
  const selectedOption = options.find((option) => option.value === value) || null;

  const handleAutocompleteChange = (
    _: any,
    newValue: DropdownSelectOption | null
  ) => {
    const mockEvent = {
      target: {
        name,
        value: newValue ? newValue.value : '',
      },
    };

    onChange(mockEvent as any);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          ...(selectProps?.sx as any)
        }}
      >
        {hasTopLabel && (
          <InputLabel
            required={required}
            sx={{
              mb: '6px',
              color: disabled ? pallet.textGrey : pallet.textLabel,
              fontSize: `${fontSizes.placeHolder} !important`,
              fontWeight: 600
            }}
          >
            {label}
          </InputLabel>
        )}
        <FormControl
          fullWidth
          required={required}
          disabled={disabled}
          sx={{ position: 'relative', m: 0, '&.MuiFormControl-root': { m: 0 } }}
          margin='none'
          error={!!helperText}
          ref={anchorRef}
        >
          {!hasTopLabel && <FormInputLabel>{label}</FormInputLabel>}

          {isSearchable ? (
            <Autocomplete
              sx={{
                '& .MuiAutocomplete-inputRoot': {
                  padding: '0 !important'
                },
              }}
              options={options}
              disabled={disabled}
              loading={loading}
              value={selectedOption}
              getOptionLabel={(option) => {
                if (typeof option.label === 'string') return option.label;
                if (option.inputValue) return option.inputValue;
                return option.value;
              }}
              isOptionEqualToValue={(option, val) => option.value === val.value}
              onChange={handleAutocompleteChange}
              renderOption={(props, option) => (
                <CustomMenuItem {...props} key={option.value} value={option.value}>
                  {option.label}
                </CustomMenuItem>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={placeholder}
                  required={required}
                  error={!!helperText}
                />
              )}
              popupIcon={<IconComp sx={{ fill: pallet.formLabelText }} />}
              clearIcon={null}
            />
          ) : (
            <Select
              displayEmpty
              value={value}
              label={label}
              name={name}
              onChange={onChange}
              IconComponent={IconComp}
              renderValue={(selected) => {
                const val = (selected as string) ?? '';
                if ((val === '' || val === undefined) && placeholder) {
                  return <Placeholder text={placeholder} />;
                }
                const found = options.find((o) => o.value === val);
                return found?.label ?? val;
              }}
              MenuProps={{
                disablePortal: true,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left'
                },
                slotProps: {
                  backdrop: {
                    sx: {
                      backgroundColor: 'transparent',
                      overflow: 'visible'
                    }
                  }
                },
                PaperProps: {
                  style: {
                    maxHeight: 320,
                    overflowY: 'auto'
                  },
                  sx: {
                    minWidth:
                      anchorRef.current && anchorRef.current.clientWidth
                        ? anchorRef.current.clientWidth
                        : undefined,
                    width:
                      anchorRef.current && anchorRef.current.clientWidth
                        ? anchorRef.current.clientWidth
                        : undefined
                  }
                }
              }}
              sx={{
                mt: 0,
                '& svg': {
                  fill: pallet.formLabelText,
                  color: pallet.formLabelText
                }
              }}
              notched={!hasTopLabel}
              {...selectProps}
            >
              {placeholder && !value && (
                <CustomMenuItem value='' disabled>
                  <Placeholder text={placeholder} />
                </CustomMenuItem>
              )}
              {options.map((option) => (
                <CustomMenuItem key={option.value} value={option.value}>
                  {option.label}
                </CustomMenuItem>
              ))}
            </Select>
          )}
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
          {hintText && (
            <Typography
              color={pallet.red2}
              fontSize='12px !important'
              fontWeight={400}
            >
              {hintText}
            </Typography>
          )}
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                right: 10,
                transform: 'translateY(-50%)',
                pointerEvents: 'none'
              }}
            >
              <CircularProgress size={18} />
            </Box>
          )}
        </FormControl>
      </Box>
    </>
  );
};

DropdownSelect.defaultProps = {
  required: true,
  disabled: false,
  loading: false
};

export default DropdownSelect;
